import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, X, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

/**
 * ReportDownloadProvider — top-level "download manager" so a long-running
 * download survives route changes. The user can click Download on the
 * Result page and then navigate anywhere in the admin panel; the download
 * keeps running in the background and a floating pill shows live progress.
 *
 * The heavy lifting now happens on the backend — this provider just fires
 * a single GET, streams the response into a Blob (with real % progress
 * from the Content-Length header) and hands the file to the browser.
 */

const Ctx = createContext(null);
export const useReportDownload = () => useContext(Ctx);

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Free the object URL a beat later so the download has time to attach.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function ReportDownloadProvider({ children }) {
  const [job, setJob] = useState(null);  // { label, done, total, phase, finished, cancelled }
  const cancelRef = useRef(false);
  const controllerRef = useRef(null);

  /**
   * start({ url, filename, label })
   * Fires a single authenticated GET, streams the response into a Blob,
   * and saves it. Shows live progress in the floating pill.
   */
  const start = useCallback(async ({ url, filename, label }) => {
    if (job && !job.finished) {
      toast.error("Another download is already in progress");
      return;
    }
    cancelRef.current = false;
    controllerRef.current = new AbortController();
    setJob({ label, done: 0, total: 0, phase: "fetching", finished: false, cancelled: false });
    try {
      const resp = await api.get(url, {
        responseType: "blob",
        signal: controllerRef.current.signal,
        onDownloadProgress: (evt) => {
          const total = evt.total || Number(evt.event?.target?.getResponseHeader?.("Content-Length")) || 0;
          setJob((j) => (j ? { ...j, done: evt.loaded || 0, total } : j));
        },
      });
      if (cancelRef.current) throw new Error("cancelled");
      const blob = resp.data;
      saveBlob(blob, filename);
      setJob((j) => (j ? { ...j, phase: "done", finished: true, total: blob.size, done: blob.size } : j));
      toast.success(`${label} · downloaded (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
      setTimeout(() => setJob((j) => (j && j.finished ? null : j)), 5000);
    } catch (e) {
      if (cancelRef.current || e?.message === "cancelled" || e?.name === "CanceledError") {
        setJob((j) => (j ? { ...j, finished: true, cancelled: true, phase: "cancelled" } : j));
        toast.error("Download cancelled");
      } else {
        setJob((j) => (j ? { ...j, finished: true, phase: "error" } : j));
        const status = e?.response?.status;
        const msg = status ? `Download failed (HTTP ${status})` : "Download failed — please retry";
        toast.error(msg, { duration: 6000 });
      }
      setTimeout(() => setJob(null), 5000);
    } finally {
      controllerRef.current = null;
    }
  }, [job]);

  const cancel = () => {
    cancelRef.current = true;
    try { controllerRef.current?.abort(); } catch { /* noop */ }
  };

  const value = { start, cancel, job, busy: !!(job && !job.finished) };
  return (
    <Ctx.Provider value={value}>
      {children}
      {job && <DownloadPill job={job} onCancel={cancel} />}
    </Ctx.Provider>
  );
}

function DownloadPill({ job, onCancel }) {
  const pct = job.total ? Math.round((job.done / job.total) * 100) : (job.phase === "done" ? 100 : 15);
  const running = !job.finished;
  const doneMb = (job.done / 1024 / 1024).toFixed(2);
  const totalMb = job.total ? (job.total / 1024 / 1024).toFixed(2) : "?";
  return (
    <div
      data-testid="report-download-pill"
      className="fixed bottom-5 right-5 z-[80] min-w-[300px] max-w-md rounded-2xl border border-[var(--line)] bg-white shadow-[0_20px_50px_-15px_rgba(0,0,0,0.35)] overflow-hidden"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
          job.phase === "done" ? "bg-emerald-100 text-emerald-700" :
          job.phase === "error" || job.phase === "cancelled" ? "bg-rose-100 text-rose-700" :
          "bg-rose-600 text-white"
        }`}>
          {job.phase === "done" ? <CheckCircle2 size={18} /> :
           job.phase === "error" || job.phase === "cancelled" ? <X size={18} /> :
           <Loader2 size={18} className="animate-spin" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            {job.phase === "done" ? `Downloaded · ${doneMb} MB` :
             job.phase === "error" ? "Failed" :
             job.phase === "cancelled" ? "Cancelled" :
             job.total ? `${doneMb} / ${totalMb} MB` : "Preparing…"}
          </div>
          <div className="font-display font-semibold text-sm truncate flex items-center gap-1.5">
            <FileDown size={13} className="shrink-0" />
            <span className="truncate">{job.label}</span>
          </div>
        </div>
        {running && (
          <button
            data-testid="report-download-cancel"
            onClick={onCancel}
            className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
            title="Cancel"
          >
            <X size={14} />
          </button>
        )}
      </div>
      <div className="h-1.5 bg-zinc-100 overflow-hidden">
        <div
          className={`h-full transition-[width] duration-200 ${job.phase === "done" ? "bg-emerald-500" : job.phase === "error" || job.phase === "cancelled" ? "bg-rose-500" : "bg-gradient-to-r from-rose-600 to-rose-400"}`}
          style={{ width: `${job.phase === "done" ? 100 : pct}%` }}
        />
      </div>
    </div>
  );
}
